import { useEffect, useMemo, useState } from "react";
import type { BlockCategory } from "@circle-react-shared/BuilderUI/DataModel.types";
import type { BlockExportMapping } from "../BuilderUI.types";
import { ELEMENT_CATEGORIES } from "../ElementsList/useCategorizedElementsList";
import { isTemplateExport } from "../Helpers/typePredicates";
import { OtherBlock } from "../constants/BlockCategories";
import { categoryIsPresent } from "./utils/categoryIsPresent";
import { processBlockCategories } from "./utils/processBlockCategories";

export interface UseBlockListProps {
  blocks: BlockExportMapping;
}

const getVisibleBlockEntries = (blocksInput: BlockExportMapping) =>
  Object.entries(blocksInput)
    .filter(([type]) => type !== "root")
    .filter(([, block]) => !block.meta.isHidden);

export const useCategorizedBlockList = ({ blocks }: UseBlockListProps) => {
  const visibleBlockEntries = useMemo(
    () => getVisibleBlockEntries(blocks),
    [blocks],
  );

  const allCategories = useMemo(() => {
    const categoriesMap = processBlockCategories({
      blocks,
      excludeCategories: ELEMENT_CATEGORIES,
    });
    let categoriesArray = Array.from(categoriesMap.values());

    const otherCategoryInMap = categoriesArray.find(
      cat => cat.name === OtherBlock.name,
    );

    if (otherCategoryInMap) {
      const hasVisibleBlocksInOther = visibleBlockEntries.some(([, block]) => {
        if (isTemplateExport(block)) {
          return false;
        }
        const blockCategories = block.meta.category ?? [];
        return (
          blockCategories.length === 0 ||
          blockCategories.some(cat => cat.name === OtherBlock.name)
        );
      });

      if (!hasVisibleBlocksInOther) {
        categoriesArray = categoriesArray.filter(
          cat => cat.name !== OtherBlock.name,
        );
      }
    }

    const otherCategories = categoriesArray.filter(
      cat => cat.name === OtherBlock.name,
    );
    const nonOtherCategories = categoriesArray.filter(
      cat => cat.name !== OtherBlock.name,
    );

    return [...nonOtherCategories, ...otherCategories].sort((a, b) => {
      if (a.name === OtherBlock.name) return 1;
      if (b.name === OtherBlock.name) return -1;
      return a.name.localeCompare(b.name, "en");
    });
  }, [blocks, visibleBlockEntries]);

  const [selectedCategory, setSelectedCategory] = useState<
    BlockCategory | undefined
  >(allCategories[0]);

  useEffect(() => {
    if (
      allCategories.length > 0 &&
      !allCategories.find(c => c.name === selectedCategory?.name)
    ) {
      setSelectedCategory(allCategories[0]);
    } else if (allCategories.length === 0) {
      setSelectedCategory(undefined);
    }
  }, [allCategories, selectedCategory]);

  const filteredBlocks = useMemo(() => {
    if (!selectedCategory) {
      return visibleBlockEntries;
    }

    return visibleBlockEntries.filter(([, block]) =>
      categoryIsPresent(block, selectedCategory),
    );
  }, [visibleBlockEntries, selectedCategory]);

  const onClickCategoryHandler = (category: BlockCategory) => {
    setSelectedCategory(category);
  };

  return {
    selectedCategory,
    allCategories,
    filteredBlocks,
    onClickCategoryHandler,
  };
};
